import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { DataTable } from '../ui/data-table';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { attendanceAPI, classAPI, classStudentAPI, userAnswerAPI, questionAPI, answerAPI } from '../../services/api';
import { TrendingUp, TrendingDown, BookOpen } from 'lucide-react';

const ClassAttendance = () => {
  const { classId } = useParams();
  const [searchParams] = useSearchParams();
  const instructorId = searchParams.get('instructorId');
  const navigate = useNavigate();
  
  console.log('ClassAttendance component mounted with classId:', classId, 'and instructorId:', instructorId);
  
  // State management
  const [classData, setClassData] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [students, setStudents] = useState([]);
  const [userAnswersData, setUserAnswersData] = useState([]);
  const [instructorTopics, setInstructorTopics] = useState([]);
  const [classDashboard, setClassDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [userAnswersLoading, setUserAnswersLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dashboard view state
  const [activeTab, setActiveTab] = useState('attendance');

  // Filter states for dashboard
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [selectedLosId, setSelectedLosId] = useState('');
  const [selectedClassTopic, setSelectedClassTopic] = useState(null);
  const [selectedDetailsLessonId, setSelectedDetailsLessonId] = useState('');
  const [selectedDetailsLosId, setSelectedDetailsLosId] = useState('');
  
  // Question modal states
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [selectedQuestionData, setSelectedQuestionData] = useState(null);
  const [questionAnswers, setQuestionAnswers] = useState([]);
  const [questionLoading, setQuestionLoading] = useState(false);
  
  // Modal states
  const [isMarkAttendanceModalOpen, setIsMarkAttendanceModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceStatus, setAttendanceStatus] = useState('present');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Date range picker states
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 6); // Default to 7 days ago
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0]; // Default to today
  });

  // Generate dates for the attendance grid based on date range
  const generateDateRange = (start, end) => {
    const dates = [];
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    
    // Ensure we don't have more than 30 days to avoid performance issues
    const daysDiff = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
    if (daysDiff > 30) {
      console.warn('Date range limited to 30 days for performance reasons');
      endDateObj.setTime(startDateObj.getTime() + (30 * 24 * 60 * 60 * 1000));
    }
    
    const currentDate = new Date(startDateObj);
    while (currentDate <= endDateObj) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const [dateRange, setDateRange] = useState(() => generateDateRange(startDate, endDate));

  // Update date range when start or end date changes
  useEffect(() => {
    // Validate date range
    if (new Date(startDate) > new Date(endDate)) {
      console.warn('Start date cannot be after end date');
      return;
    }
    
    const newDateRange = generateDateRange(startDate, endDate);
    setDateRange(newDateRange);
  }, [startDate, endDate]);

  // Create attendance grid data structure
  const createAttendanceGrid = () => {
    const grid = {};
    
    students.forEach(student => {
      const studentKey = `${student.first_name || student.student?.first_name || ''} ${student.last_name || student.student?.last_name || ''}`.trim();
      const studentId = student.student_id || student.student?.id;
      
      grid[studentKey] = {
        studentId: studentId,
        studentData: student,
        attendance: {}
      };
      
      // Initialize all dates with 'absent' status
      dateRange.forEach(date => {
        grid[studentKey].attendance[date] = 'absent';
      });
    });

    // Fill in actual attendance data
    attendanceData.forEach(record => {
      const recordDate = record.date || record.marked_at;
      const studentId = record.student_id || record.student?.id;
      let status = 'absent'; // default
      
      // Handle numeric status values (0 = absent, 1 = present, 2 = excused)
      if (record.status === 1) {
        status = 'present';
      } else if (record.status === 2) {
        status = 'excused';
      } else {
        status = 'absent';
      }
      
      // Find the student in our grid
      Object.keys(grid).forEach(studentName => {
        if (grid[studentName].studentId === studentId) {
          if (recordDate && dateRange.includes(recordDate)) {
            grid[studentName].attendance[recordDate] = status;
          }
        }
      });
    });

    return grid;
  };

  const attendanceGrid = createAttendanceGrid();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      present: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      late: 'bg-yellow-100 text-yellow-800',
      excused: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusSymbol = (status) => {
    const symbols = {
      present: '✓',
      absent: '✗',
      excused: 'E'
    };
    return symbols[status] || '?';
  };

  const handleQuickMarkAttendance = async (student, date, currentStatus) => {
    try {
      let newStatus;
      let statusCode;
      
      // Cycle through statuses: absent -> present -> excused -> absent
      if (currentStatus === 'absent') {
        newStatus = 'present';
        statusCode = 1;
      } else if (currentStatus === 'present') {
        newStatus = 'excused';
        statusCode = 2;
      } else if (currentStatus === 'excused') {
        newStatus = 'absent';
        statusCode = 0;
      } else {
        newStatus = 'present';
        statusCode = 1;
      }
      
      const attendancePayload = {
        class_id: parseInt(classId),
        student_id: parseInt(student.studentId),
        lesson_id : 0,
        marked_at: date,
        status: statusCode,
      };
        console.log('Marking attendance:', attendancePayload);
      await attendanceAPI.markAttendance(attendancePayload);
      await fetchAttendance(); // Refresh data
      
    } catch (error) {
      console.error('Error marking attendance:', error);
      setError('Failed to mark attendance. Please try again.');
    }
  };

  useEffect(() => {
    if (classId) {
      fetchClassDetails();
      fetchStudents();
      fetchAttendance();
      // fetchUserAnswers();
      fetchClassDashboard();
    }
    if (instructorId) {
      fetchInstructorTopics(instructorId);
    }
  }, [classId, instructorId]);

  const fetchInstructorTopics = async (instructorId) => {
    try {
      console.log('Fetching instructor topics for instructor ID:', instructorId);
      const response = await classAPI.getClassesByInstructorId(instructorId);
      console.log('Instructor classes response:', response);
      setInstructorTopics(response.topics || []);
    } catch (error) {
      console.error('Error fetching instructor topics:', error);
    }
  };

  const fetchClassDashboard = async () => {
    try {
      setDashboardLoading(true);
      const response = await classAPI.getClassDashboard(classId);
      console.log('Class dashboard data:', response.data);
      setClassDashboard(response.data || null);
    } catch (error) {
      console.error('Error fetching class dashboard:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchClassDetails = async () => {
    try {
      const response = await classAPI.getClassById(classId);
      setClassData(response.data);
    } catch (error) {
      console.error('Error fetching class details:', error);
      setError('Failed to load class details.');
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await classStudentAPI.getStudentsInClass(classId);
      setStudents(response.data || []);
      console.log('Students data:', response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load students.');
    }
  };

  const fetchAttendance = async () => {
    try {
      setAttendanceLoading(true);
      const response = await attendanceAPI.getAttendanceForClass(classId);
      console.log('Attendance data:', response.data);
      setAttendanceData(response.data || []);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setError('Failed to load attendance data.');
    } finally {
      setAttendanceLoading(false);
      setLoading(false);
    }
  };

  const fetchUserAnswers = async () => {
    try {
      setUserAnswersLoading(true);
      const response = await userAnswerAPI.getUserAnswersByClassId(classId);
      console.log('User answers data:', response.data);
      setUserAnswersData(response.data || []);
    } catch (error) {
      console.error('Error fetching user answers:', error);
      setError('Failed to load user answers data.');
    } finally {
      setUserAnswersLoading(false);
    }
  };

  const downloadAttendance = () => {
    if (!attendanceGrid || Object.keys(attendanceGrid).length === 0) {
      alert('No attendance data to download');
      return;
    }

    try {
      // Prepare data for export
      const exportData = Object.keys(attendanceGrid).map(studentName => {
        const student = attendanceGrid[studentName];
        const rowData = {
          'Student Name': studentName,
          'Email': student.email || 'N/A'
        };

        // Add attendance for each date
        dateRange.forEach(date => {
          const status = student.attendance[date];
          rowData[formatDate(date)] = status === 'present' ? 'Present' : status === 'absent' ? 'Absent' : status === 'excused' ? 'Excused' : '-';
        });

        return rowData;
      });

      // Generate CSV
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header];
            // Escape commas and quotes in values
            if (value && (value.toString().includes(',') || value.toString().includes('"'))) {
              return `"${value.toString().replace(/"/g, '""')}"`;
            }
            return value || '';
          }).join(',')
        )
      ].join('\n');

      // Download CSV with UTF-8 BOM for proper encoding
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `class_attendance_${classData?.title || classId}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading attendance:', err);
      alert('Failed to download attendance data');
    }
  };

  const handleMarkAttendance = async () => {
    if (!selectedStudent || !attendanceDate) return;

    try {
      setIsSubmitting(true);
      
      // Convert status string to numeric code
      let statusCode;
      switch (attendanceStatus) {
        case 'present':
          statusCode = 1;
          break;
        case 'excused':
          statusCode = 2;
          break;
        case 'absent':
        default:
          statusCode = 0;
          break;
      }
      
      const attendancePayload = {
        class_id: parseInt(classId),
        student_id: selectedStudent.student_id || selectedStudent.student.id,
        marked_at: attendanceDate,
        status: statusCode,
        marked_by: 'admin' // You might want to get this from user context
      };

      console.log('Marking attendance:', attendancePayload);
      
      await attendanceAPI.markAttendance(attendancePayload);
      
      // Refresh attendance data
      await fetchAttendance();
      
      // Close modal and reset state
      setIsMarkAttendanceModalOpen(false);
      setSelectedStudent(null);
      setAttendanceStatus('present');
      
    } catch (error) {
      console.error('Error marking attendance:', error);
      setError('Failed to mark attendance. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openMarkAttendanceModal = (student) => {
    setSelectedStudent(student);
    setIsMarkAttendanceModalOpen(true);
  };

  const handleGoBack = () => {
    // Get user data from storage to check role
    const storedUser = localStorage.getItem('icer_admin_user') || 
                      localStorage.getItem('adminUser') || 
                      sessionStorage.getItem('adminUser');
    
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.role === 'instructor') {
          navigate('/instructor');
        } else {
          navigate(`/classes/${classId}`);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        // Fallback to class details if user data is corrupted
        navigate(`/classes/${classId}`);
      }
    } else {
      // Fallback to class details if no user data found
      navigate(`/classes/${classId}`);
    }
  };

  // Process user answers data for dashboard - filtered by instructor topics
  const processDashboardData = () => {
    if (!userAnswersData.length) return { topics: [], lessons: [], losOptions: [], filteredAnswers: [], metrics: {} };

    // Get instructor topic IDs for filtering
    const instructorTopicIds = instructorTopics.map(t => t.topic_id);
    console.log('Instructor topic IDs:', instructorTopicIds);

    // Extract all topics, lessons, and LOS options
    const topics = [];
    const lessons = [];
    const losOptions = [];
    const allAnswers = [];

    userAnswersData.forEach(topicData => {
      // Filter by instructor topics if instructorId is present
      if (instructorId && instructorTopicIds.length > 0) {
        if (!instructorTopicIds.includes(topicData.topic_id)) {
          return; // Skip topics not assigned to this instructor
        }
      }

      // Add topic to options
      topics.push({
        id: topicData.topic_id,
        title: topicData.topic_title,
        description: topicData.topic_description,
        course_id: topicData.course_id,
        course_title: topicData.course_title
      });
      // Add lesson to options
      lessons.push({
        id: topicData.lesson_id,
        title: topicData.lesson_title,
        description: topicData.lesson_description,
        topic_id: topicData.topic_id,
        topic_title: topicData.topic_title
      });

      // Process user answers
      topicData.user_answers.forEach(answer => {
        // Add LOS to options if not already added
        const existingLos = losOptions.find(los => los.id === answer.los_id);
        if (!existingLos) {
          losOptions.push({
            id: answer.los_id,
            name: answer.los_name,
            lesson_id: topicData.lesson_id,
            lesson_title: topicData.lesson_title
          });
        }

        // Add answer to all answers with context
        allAnswers.push({
          ...answer,
          topic_id: topicData.topic_id,
          topic_title: topicData.topic_title,
          lesson_id: topicData.lesson_id,
          lesson_title: topicData.lesson_title,
          course_id: topicData.course_id,
          course_title: topicData.course_title
        });
      });
    });

    return {
      topics: topics,
      lessons: lessons,
      losOptions: losOptions,
      allAnswers: allAnswers
    };
  };

  const dashboardData = processDashboardData();

  // Filter data based on selected filters
  const getFilteredData = () => {
    let filteredAnswers = dashboardData.allAnswers || [];

    // Filter by topic
    if (selectedTopicId) {
      filteredAnswers = filteredAnswers.filter(answer => answer.topic_id.toString() === selectedTopicId);
    }

    // Filter by lesson
    if (selectedLessonId) {
      filteredAnswers = filteredAnswers.filter(answer => answer.lesson_id.toString() === selectedLessonId);
    }

    // Filter by LOS
    if (selectedLosId) {
      filteredAnswers = filteredAnswers.filter(answer => answer.los_id.toString() === selectedLosId);
    }

    // Calculate metrics
    const totalAnswers = filteredAnswers.length;
    const totalPoints = filteredAnswers.reduce((sum, answer) => sum + (parseFloat(answer.point) || 0), 0);
    const uniqueQuestions = new Set(filteredAnswers.map(answer => answer.question_id)).size;
    const uniqueStudents = new Set(filteredAnswers.map(answer => answer.user_answer_id)).size;
    const correctAnswers = filteredAnswers.filter(answer => parseFloat(answer.point) > 0).length;

    return {
      filteredAnswers,
      metrics: {
        totalAnswers,
        totalPoints,
        uniqueQuestions,
        uniqueStudents,
        correctAnswers,
        averageScore: totalAnswers > 0 ? ((correctAnswers / totalAnswers) * 100).toFixed(1) : '0',
        averagePoints: totalAnswers > 0 ? (totalPoints / totalAnswers).toFixed(2) : '0'
      }
    };
  };

  const filteredData = getFilteredData();

  // Get available lessons based on selected topic
  const getAvailableLessons = () => {
    if (!selectedTopicId) return dashboardData.lessons || [];
    return (dashboardData.lessons || []).filter(lesson => lesson.topic_id.toString() === selectedTopicId);
  };

  // Get available LOS based on selected lesson
  const getAvailableLos = () => {
    if (!selectedLessonId) return dashboardData.losOptions || [];
    return (dashboardData.losOptions || []).filter(los => los.lesson_id.toString() === selectedLessonId);
  };

  // Define columns for attendance DataTable
  const attendanceColumns = [
    {
      accessorKey: 'student_id',
      header: 'Student ID',
      sortable: true,
      cell: (attendance) => attendance.student_id || attendance.student?.id || 'N/A'
    },
    {
      accessorKey: 'student_name',
      header: 'Student Name',
      sortable: true,
      cell: (attendance) => {
        if (attendance.student_name) return attendance.student_name;
        if (attendance.student) {
          return `${attendance.student.first_name || ''} ${attendance.student.last_name || ''}`.trim();
        }
        if (attendance.first_name || attendance.last_name) {
          return `${attendance.first_name || ''} ${attendance.last_name || ''}`.trim();
        }
        return 'N/A';
      }
    },
    {
      accessorKey: 'date',
      header: 'Date',
      sortable: true,
      cell: (attendance) => {
        const date = attendance.date || attendance.attendance_date;
        return date ? new Date(date).toLocaleDateString() : 'N/A';
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      sortable: true,
      cell: (attendance) => {
        let status = attendance.status || attendance.attendance_status;
        
        // Handle numeric status values (0 = absent, 1 = present, 2 = excused)
        if (typeof status === 'number') {
          if (status === 1) {
            status = 'present';
          } else if (status === 2) {
            status = 'excused';
          } else {
            status = 'absent';
          }
        }
        
        // Ensure status is a string
        const statusString = String(status || 'unknown').toLowerCase();
        
        const statusColors = {
          present: 'bg-green-100 text-green-800',
          absent: 'bg-red-100 text-red-800',
          late: 'bg-yellow-100 text-yellow-800',
          excused: 'bg-blue-100 text-blue-800'
        };
        
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[statusString] || 'bg-gray-100 text-gray-800'}`}>
            {statusString ? statusString.charAt(0).toUpperCase() + statusString.slice(1) : 'Unknown'}
          </span>
        );
      }
    },
    {
      accessorKey: 'marked_at',
      header: 'Marked At',
      sortable: true,
      cell: (attendance) => {
        const markedAt = attendance.marked_at || attendance.created_at;
        return markedAt ? new Date(markedAt).toLocaleString() : 'N/A';
      }
    }
  ];

  // Define columns for students (to show who can have attendance marked)
  const studentColumns = [
    {
      accessorKey: 'id',
      header: 'Student ID',
      sortable: true,
      cell: (student) => student.id || student.student_id || 'N/A'
    },
    {
      accessorKey: 'first_name',
      header: 'First Name',
      sortable: true,
      cell: (student) => student.first_name || student.student?.first_name || 'N/A'
    },
    {
      accessorKey: 'last_name',
      header: 'Last Name',
      sortable: true,
      cell: (student) => student.last_name || student.student?.last_name || 'N/A'
    },
    {
      accessorKey: 'email',
      header: 'Email',
      sortable: true,
      cell: (student) => student.email || student.student?.email || 'N/A'
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: (student) => (
        <Button
          size="sm"
          onClick={() => openMarkAttendanceModal(student)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Mark Attendance
        </Button>
      ),
    },
  ];
  
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-9">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-9">
        <div className="text-center py-8">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={handleGoBack} className="bg-blue-600 hover:bg-blue-700">
            Back to Class Details
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-9">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleGoBack}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              title="Back to class details"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Class Dashboard</h1>
              <p className="text-lg text-gray-600">
                {classData ? `${classData.title} - Management Dashboard` : 'Class Management Dashboard'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('attendance')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'attendance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Attendance Management
            </button>
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Student Progress Dashboard
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'attendance' && (
        <>
          {/* Weekly Attendance Grid */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Attendance Overview</h2>
              <p className="text-purple-100">Quick view and edit attendance for selected date range</p>
            </div>
            
            {/* Date Range Picker and Download Button */}
            <div className="flex items-center space-x-4">
              <Button
                onClick={downloadAttendance}
                className="bg-white text-purple-600 hover:bg-purple-50 font-medium"
                size="sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Excel
              </Button>
              <div className="flex items-center space-x-2">
                <label className="text-purple-100 text-sm font-medium">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-1 rounded-md border border-purple-300 bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-purple-100 text-sm font-medium">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-1 rounded-md border border-purple-300 bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                />
              </div>
              <div className="text-purple-100 text-xs">
                {new Date(startDate) > new Date(endDate) ? (
                  <span className="text-red-200">Invalid range</span>
                ) : (
                  <>
                    {dateRange.length} day{dateRange.length !== 1 ? 's' : ''}
                    {dateRange.length > 30 && <span className="text-yellow-200 ml-1">(Limited to 30)</span>}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6 max-w-full overflow-x-auto">
          <div className="overflow-x-auto shadow-lg rounded-lg">
            <table className="min-w-full bg-white border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-300 sticky left-0 bg-gray-50 z-10">
                    Student Name
                  </th>
                  {dateRange.map((date) => (
                    <th
                      key={date}
                      className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300"
                    >
                      {formatDate(date)}
                      
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.keys(attendanceGrid).map((studentName, rowIndex) => (
                  <tr
                    key={studentName}
                    className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
                  >
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 border-r border-gray-300 sticky left-0 bg-inherit z-10">
                      {studentName || 'Unknown Student'}
                    </td>
                    {dateRange.map((date) => {
                      const status = attendanceGrid[studentName].attendance[date];
                      const student = attendanceGrid[studentName];
                      
                      return (
                        <td
                          key={`${studentName}-${date}`}
                          className="px-2 py-1 text-sm text-center border-gray-200"
                        >
                          <button
                            onClick={() => handleQuickMarkAttendance(student, date, status)}
                            className={`px-2 py-1 rounded-full text-xs font-medium transition-colors hover:opacity-80 ${getStatusColor(status)}`}
                            title={`${status} - Click to cycle through statuses`}
                          >
                            {getStatusSymbol(status)}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <div className="flex flex-wrap gap-4 mb-3">
              <div className="flex items-center">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">✓</span>
                Present
              </div>
              <div className="flex items-center">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mr-2">✗</span>
                Absent
              </div>
              <div className="flex items-center">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">E</span>
                Excused
              </div>
            </div>
            <div className="text-xs text-gray-500 mb-3">
              Click on attendance status to cycle through: Absent → Present → Excused → Absent
            </div>
            
            {/* Quick Date Range Buttons */}
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                onClick={() => {
                  const today = new Date();
                  const weekAgo = new Date(today);
                  weekAgo.setDate(today.getDate() - 6);
                  setStartDate(weekAgo.toISOString().split('T')[0]);
                  setEndDate(today.toISOString().split('T')[0]);
                }}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-xs hover:bg-purple-200 transition-colors"
              >
                Last 7 Days
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const monthAgo = new Date(today);
                  monthAgo.setDate(today.getDate() - 13);
                  setStartDate(monthAgo.toISOString().split('T')[0]);
                  setEndDate(today.toISOString().split('T')[0]);
                }}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-xs hover:bg-purple-200 transition-colors"
              >
                Last 14 Days
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  const monthAgo = new Date(today);
                  monthAgo.setMonth(today.getMonth() - 1);
                  setStartDate(monthAgo.toISOString().split('T')[0]);
                  setEndDate(today.toISOString().split('T')[0]);
                }}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-xs hover:bg-purple-200 transition-colors"
              >
                Last 30 Days
              </button>
              <button
                onClick={() => {
                  const today = new Date();
                  setStartDate(today.toISOString().split('T')[0]);
                  setEndDate(today.toISOString().split('T')[0]);
                }}
                className="px-3 py-1 bg-green-100 text-green-700 rounded-md text-xs hover:bg-green-200 transition-colors"
              >
                Today Only
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Attendance Records Section */}
      {/* <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-green-600 px-6 py-4">
          <h2 className="text-2xl font-bold text-white">Attendance Records</h2>
          <p className="text-blue-100">View all attendance records for this class</p>
        </div>
        
        <div className="p-6">
          <DataTable
            columns={attendanceColumns}
            data={attendanceData}
            searchKey="student_name"
            searchPlaceholder="Search by student name..."
            loading={attendanceLoading}
            emptyMessage="No attendance records found for this class."
          />
        </div>
      </div> */}

      {/* Students Section for Marking Attendance */}
      {/* <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-blue-600 px-6 py-4">
          <h2 className="text-2xl font-bold text-white">Mark Attendance</h2>
          <p className="text-green-100">Mark attendance for students in this class</p>
        </div>
        
        <div className="p-6">
          <DataTable
            columns={studentColumns}
            data={students}
            searchKey="first_name"
            searchPlaceholder="Search students..."
            loading={false}
            emptyMessage="No students enrolled in this class."
          />
        </div>
      </div> */}

        </>
      )}

      {/* Dashboard Content */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Class Dashboard Overview */}
          {dashboardLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading dashboard data...</p>
            </div>
          ) : classDashboard ? (
            <>
              {/* Class Info and Attendance Section */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-xl font-bold mb-4 text-gray-800">CLASS DASHBOARD</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-700">Class Information</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Class ID:</span>
                        <span className="text-gray-800">{classDashboard.class_info?.class_id || classId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Class Name:</span>
                        <span className="text-gray-800">{classDashboard.class_info?.class_name || classData?.title || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-700">Attendance Statistics</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Total Students:</span>
                        <span className="text-gray-800 font-semibold">{classDashboard.attendance?.total_students || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Students Attended:</span>
                        <span className="text-green-600 font-semibold">{classDashboard.attendance?.students_attended || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Attendance Rate:</span>
                        <span className={`font-bold text-lg ${
                          (classDashboard.attendance?.attendance_rate || 0) >= 80 ? 'text-green-600' :
                          (classDashboard.attendance?.attendance_rate || 0) >= 60 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {classDashboard.attendance?.attendance_rate || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Topic Performance Chart Section */}
              {(() => {
                const instructorTopicIds = instructorTopics.map(t => t.topic_id);
                let topicPerformance = classDashboard.topic_performance || [];
                
                if (instructorId && instructorTopicIds.length > 0) {
                  topicPerformance = topicPerformance.filter(tp => instructorTopicIds.includes(tp.topic_id));
                }

                if (topicPerformance.length === 0) {
                  return (
                    <div className="bg-white p-6 rounded-lg shadow-md mb-6 text-center">
                      <p className="text-gray-600">No topic performance data available for this class.</p>
                    </div>
                  );
                }

                return (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                    <h3 className="text-lg font-semibold mb-4">Topic Performance Comparison</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {instructorId && instructorTopicIds.length > 0 
                        ? `Showing ${topicPerformance.length} topic(s) assigned to you`
                        : 'Compare this class\'s performance against overall class averages'
                      }
                    </p>

                    {/* Detailed Table */}
                    <div className="mt-6">
                      <h4 className="text-sm font-medium mb-3 text-gray-600">Detailed Performance Breakdown</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border p-3 text-left">Topic</th>
                              <th className="border p-3 text-center">Class Score</th>
                              <th className="border p-3 text-center">Overall Average</th>
                              <th className="border p-3 text-center">Performance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topicPerformance.map((topic, index) => {
                              const classScore = (topic.average_score * 100).toFixed(1);
                              const overallAverage = (topic.class_average_score * 100).toFixed(1);
                              const diff = (classScore - overallAverage).toFixed(1);
                              const isAboveAverage = diff > 0;
                              
                              return (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="border p-3 font-medium">{topic.topic_title}</td>
                                  <td className="border p-3 text-center">
                                    <span className="font-semibold text-blue-600">{classScore}%</span>
                                  </td>
                                  <td className="border p-3 text-center">
                                    <span className="font-semibold text-green-600">{overallAverage}%</span>
                                  </td>
                                  <td className="border p-3 text-center">
                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                                      isAboveAverage 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {isAboveAverage ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                                      {isAboveAverage ? '+' : ''}{diff}%
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* User Answers Summary Section */}
              {(() => {
                const instructorTopicIds = instructorTopics.map(t => t.topic_id);
                let userAnswersSummary = classDashboard.user_answers_summary || [];
                
                if (instructorId && instructorTopicIds.length > 0) {
                  userAnswersSummary = userAnswersSummary.filter(item => instructorTopicIds.includes(item.topic_id));
                }

                if (userAnswersSummary.length === 0) {
                  return (
                    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                      <h3 className="text-lg font-semibold mb-4">User Answers Summary</h3>
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-16 h-16 mx-auto mb-2 text-gray-400" />
                        <p>No user answers data available.</p>
                      </div>
                    </div>
                  );
                }

                // Group lessons by topic
                const groupedByTopic = {};
                userAnswersSummary.forEach(lesson => {
                  const topic = lesson.topic_title || 'Unknown Topic';
                  if (!groupedByTopic[topic]) {
                    groupedByTopic[topic] = [];
                  }
                  groupedByTopic[topic].push(lesson);
                });

                const availableTopics = Object.keys(groupedByTopic);
                const currentSelectedTopic = selectedClassTopic && availableTopics.includes(selectedClassTopic)
                  ? selectedClassTopic
                  : availableTopics[0];

                return (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h3 className="text-lg font-semibold">User Answers Summary</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Total Lessons</p>
                        <p className="text-2xl font-bold text-blue-600">{userAnswersSummary.length}</p>
                      </div>
                    </div>

                    {/* Topic Filter Buttons */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {Object.keys(groupedByTopic).map(topic => (
                        <button
                          key={topic}
                          onClick={() => setSelectedClassTopic(topic)}
                          className={`px-4 py-2 rounded-lg font-medium transition-all ${
                            currentSelectedTopic === topic
                              ? 'bg-gray-600 text-white shadow-lg transform scale-105'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {topic}
                          <span className="ml-2 px-2 py-0.5 bg-white bg-opacity-30 rounded-full text-xs">
                            {groupedByTopic[topic].length}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Display only selected topic */}
                    <div className="space-y-6">
                      {currentSelectedTopic && groupedByTopic[currentSelectedTopic] && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-blue-100 px-4 py-3 border-b">
                            <h4 className="font-semibold text-blue-900">{currentSelectedTopic}</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              {groupedByTopic[currentSelectedTopic].length} lesson{groupedByTopic[currentSelectedTopic].length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-gray-50 border-b">
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lesson</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Correct</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Incorrect</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Accuracy</th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupedByTopic[currentSelectedTopic].map((lesson, index) => (
                                  <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                                    <td className="px-4 py-3">
                                      <div className="font-medium text-gray-900">{lesson.lesson_title}</div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {lesson.total_answers}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        {lesson.correct_answers}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        {lesson.incorrect_answers}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <div className="flex items-center justify-center gap-2">
                                        <div className="w-16 bg-gray-200 rounded-full h-2">
                                          <div
                                            className={`h-2 rounded-full ${
                                              lesson.accuracy_rate >= 70 ? 'bg-green-500' :
                                              lesson.accuracy_rate >= 50 ? 'bg-yellow-500' :
                                              'bg-red-500'
                                            }`}
                                            style={{ width: `${lesson.accuracy_rate}%` }}
                                          ></div>
                                        </div>
                                        <span className={`font-semibold text-sm ${
                                          lesson.accuracy_rate >= 70 ? 'text-green-600' :
                                          lesson.accuracy_rate >= 50 ? 'text-yellow-600' :
                                          'text-red-600'
                                        }`}>
                                          {lesson.accuracy_rate}%
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* User Answers Details Section */}
              {(() => {
                const instructorTopicIds = instructorTopics.map(t => t.topic_id);
                let userAnswersDetails = classDashboard.user_answers_details || [];
                
                if (instructorId && instructorTopicIds.length > 0) {
                  userAnswersDetails = userAnswersDetails.filter(item => instructorTopicIds.includes(item.topic_id));
                }

                if (userAnswersDetails.length === 0) {
                  return (
                    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                      <h3 className="text-lg font-semibold mb-4">User Answers Details</h3>
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-16 h-16 mx-auto mb-2 text-gray-400" />
                        <p>No user answers details available.</p>
                      </div>
                    </div>
                  );
                }

                // Get unique lessons for filter
                const uniqueLessons = [];
                const lessonMap = new Map();
                userAnswersDetails.forEach(item => {
                  if (!lessonMap.has(item.lesson_id)) {
                    lessonMap.set(item.lesson_id, {
                      id: item.lesson_id,
                      title: item.lesson_title
                    });
                    uniqueLessons.push({ id: item.lesson_id, title: item.lesson_title });
                  }
                });

                // Get unique LOS for filter (based on selected lesson)
                const uniqueLos = [];
                const losMap = new Map();
                userAnswersDetails.forEach(item => {
                  if (selectedDetailsLessonId && item.lesson_id.toString() !== selectedDetailsLessonId) return;
                  if (!losMap.has(item.los_id)) {
                    losMap.set(item.los_id, {
                      id: item.los_id,
                      name: item.los_name
                    });
                    uniqueLos.push({ id: item.los_id, name: item.los_name });
                  }
                });

                // Filter data based on selected lesson and LOS
                let filteredDetails = userAnswersDetails;
                if (selectedDetailsLessonId) {
                  filteredDetails = filteredDetails.filter(item => item.lesson_id.toString() === selectedDetailsLessonId);
                }
                if (selectedDetailsLosId) {
                  filteredDetails = filteredDetails.filter(item => item.los_id.toString() === selectedDetailsLosId);
                }

                // Handle question click
                const handleQuestionClick = async (item) => {
                  try {
                    setQuestionLoading(true);
                    setIsQuestionModalOpen(true);
                    setSelectedQuestionData(item);
                    
                    // Fetch question details and answers
                    const questionResponse = await questionAPI.getQuestionById(item.question_id);
                    const answersResponse = await answerAPI.getAnswersByQuestionId(item.question_id);
                    
                    setSelectedQuestionData({
                      ...item,
                      questionDetails: questionResponse.data
                    });
                    setQuestionAnswers(answersResponse.data || []);
                  } catch (error) {
                    console.error('Error fetching question details:', error);
                  } finally {
                    setQuestionLoading(false);
                  }
                };

                return (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h3 className="text-lg font-semibold">User Answers Details</h3>
                        <p className="text-sm text-gray-600">Question-level performance breakdown</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Total Questions</p>
                        <p className="text-2xl font-bold text-purple-600">{filteredDetails.length}</p>
                      </div>
                    </div>

                    {/* Lesson and LOS Filters */}
                    <div className="flex flex-wrap gap-4 mb-6">
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Lesson</label>
                        <select
                          value={selectedDetailsLessonId}
                          onChange={(e) => {
                            setSelectedDetailsLessonId(e.target.value);
                            setSelectedDetailsLosId(''); // Reset LOS when lesson changes
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">All Lessons</option>
                          {uniqueLessons.map(lesson => (
                            <option key={lesson.id} value={lesson.id.toString()}>
                              {lesson.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by LOS</label>
                        <select
                          value={selectedDetailsLosId}
                          onChange={(e) => setSelectedDetailsLosId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          disabled={!selectedDetailsLessonId}
                        >
                          <option value="">All LOS</option>
                          {uniqueLos.map(los => (
                            <option key={los.id} value={los.id.toString()}>
                              {los.name?.split(':')[0] || los.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {(selectedDetailsLessonId || selectedDetailsLosId) && (
                        <div className="flex items-end">
                          <button
                            onClick={() => {
                              setSelectedDetailsLessonId('');
                              setSelectedDetailsLosId('');
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                          >
                            Clear Filters
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Display filtered questions */}
                    <div className="space-y-6">
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-purple-100 px-4 py-3 border-b">
                          <h4 className="font-semibold text-purple-900">
                            {selectedDetailsLessonId 
                              ? uniqueLessons.find(l => l.id.toString() === selectedDetailsLessonId)?.title || 'Selected Lesson'
                              : 'All Lessons'
                            }
                          </h4>
                          <p className="text-sm text-purple-700 mt-1">
                            {filteredDetails.length} question{filteredDetails.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-50 border-b">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lesson</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">LOS</th>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Question</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Correct</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Incorrect</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Accuracy</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredDetails.map((item, index) => (
                                <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                                  <td className="px-4 py-3">
                                    <div className="font-medium text-gray-900 text-sm">{item.lesson_title}</div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="text-sm text-gray-700 max-w-xs truncate" title={item.los_name}>
                                      {item.los_name?.split(':')[0] || item.los_name}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div 
                                      className="text-sm text-blue-600 max-w-[300px] truncate cursor-pointer hover:text-blue-800 hover:underline" 
                                      title="Click to view question details"
                                      onClick={() => handleQuestionClick(item)}
                                      dangerouslySetInnerHTML={{ 
                                        __html: item.question_text?.length > 80
                                          ? item.question_text.substring(0, 80) + '...' 
                                          : item.question_text 
                                      }}
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {item.total_answers}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {item.correct_answers}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      {item.incorrect_answers}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                      <div className="w-16 bg-gray-200 rounded-full h-2">
                                        <div
                                          className={`h-2 rounded-full ${
                                            item.accuracy_rate >= 70 ? 'bg-green-500' :
                                            item.accuracy_rate >= 50 ? 'bg-yellow-500' :
                                            'bg-red-500'
                                          }`}
                                          style={{ width: `${item.accuracy_rate}%` }}
                                        ></div>
                                      </div>
                                      <span className={`font-semibold text-sm ${
                                        item.accuracy_rate >= 70 ? 'text-green-600' :
                                        item.accuracy_rate >= 50 ? 'text-yellow-600' :
                                        'text-red-600'
                                      }`}>
                                        {item.accuracy_rate}%
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Dashboard Data</h3>
              <p className="text-gray-600">Unable to load class dashboard data.</p>
            </div>
          )}
        </div>
      )}

      {/* Mark Attendance Modal */}
      <Modal 
        isOpen={isMarkAttendanceModalOpen} 
        onClose={() => {
          setIsMarkAttendanceModalOpen(false);
          setSelectedStudent(null);
          setAttendanceStatus('present');
        }}
        className="max-w-md"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900">Mark Attendance</h2>
        </ModalHeader>
        
        <ModalBody className="space-y-4">
          {selectedStudent && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-700">
                <strong>Student:</strong> {selectedStudent.first_name || selectedStudent.student?.first_name} {selectedStudent.last_name || selectedStudent.student?.last_name}
              </p>
              <p className="text-sm text-blue-600">
                <strong>Email:</strong> {selectedStudent.email || selectedStudent.student?.email}
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
            <input
              type="date"
              value={attendanceDate}
              onChange={(e) => setAttendanceDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={attendanceStatus}
              onChange={(e) => setAttendanceStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="present">Present</option>
              <option value="absent">Absent</option>
              <option value="late">Late</option>
              <option value="excused">Excused</option>
            </select>
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button 
            variant="outline" 
            onClick={() => {
              setIsMarkAttendanceModalOpen(false);
              setSelectedStudent(null);
              setAttendanceStatus('present');
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleMarkAttendance}
            disabled={!selectedStudent || !attendanceDate || isSubmitting}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? 'Marking...' : 'Mark Attendance'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Question Details Modal */}
      <Modal 
        isOpen={isQuestionModalOpen} 
        onClose={() => {
          setIsQuestionModalOpen(false);
          setSelectedQuestionData(null);
          setQuestionAnswers([]);
        }}
        className="max-w-3xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900">Question Details</h2>
        </ModalHeader>
        
        <ModalBody className="space-y-4">
          {questionLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <p className="mt-4 text-gray-600">Loading question details...</p>
            </div>
          ) : selectedQuestionData ? (
            <>
              {/* Question Info */}
              <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
                <div className="mb-3">
                  <span className="text-sm font-medium text-purple-700">Lesson:</span>
                  <span className="ml-2 text-gray-800">{selectedQuestionData.lesson_title}</span>
                </div>
                <div className="mb-3">
                  <span className="text-sm font-medium text-purple-700">LOS:</span>
                  <span className="ml-2 text-gray-800">{selectedQuestionData.los_name?.split(':')[0] || selectedQuestionData.los_name}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-purple-700 block mb-2">Question:</span>
                  <div 
                    className="text-gray-800 bg-white p-3 rounded border"
                    dangerouslySetInnerHTML={{ __html: selectedQuestionData.question_text }}
                  />
                </div>
              </div>

              {/* Performance Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <p className="text-sm text-blue-600 font-medium">Total Answers</p>
                  <p className="text-2xl font-bold text-blue-800">{selectedQuestionData.total_answers}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <p className="text-sm text-green-600 font-medium">Correct</p>
                  <p className="text-2xl font-bold text-green-800">{selectedQuestionData.correct_answers}</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg text-center">
                  <p className="text-sm text-red-600 font-medium">Incorrect</p>
                  <p className="text-2xl font-bold text-red-800">{selectedQuestionData.incorrect_answers}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <p className="text-sm text-purple-600 font-medium">Accuracy</p>
                  <p className={`text-2xl font-bold ${
                    selectedQuestionData.accuracy_rate >= 70 ? 'text-green-600' :
                    selectedQuestionData.accuracy_rate >= 50 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>{selectedQuestionData.accuracy_rate}%</p>
                </div>
              </div>

              {/* Answers Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3">Answer Options</h4>
                {questionAnswers.length > 0 ? (
                  <div className="space-y-2">
                    {questionAnswers.map((answer, index) => (
                      <div 
                        key={answer.id || index}
                        className={`p-3 rounded-lg border-2 ${
                          answer.is_correct 
                            ? 'bg-green-50 border-green-400' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                            answer.is_correct 
                              ? 'bg-green-500 text-white' 
                              : 'bg-gray-300 text-gray-700'
                          }`}>
                            {String.fromCharCode(65 + index)}
                          </span>
                          <div className="flex-1">
                            <div 
                              className="text-gray-800"
                              dangerouslySetInnerHTML={{ __html: answer.option_text}}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No answer options available.</p>
                )}
              </div>
            </>
          ) : (
            <p className="text-gray-500 text-center py-4">No question data available.</p>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button 
            variant="outline" 
            onClick={() => {
              setIsQuestionModalOpen(false);
              setSelectedQuestionData(null);
              setQuestionAnswers([]);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ClassAttendance;
